import { createRouter, createWebHistory } from 'vue-router';

import Swal from 'sweetalert2';
import i18n, { setI18nLocale } from '@/api/i18n';
import apiClient from '@/api/index';
import { setLoading } from '@/plugins/loading';

const MainLayout = () => import('@/views/MainLayout.vue');
const HomeView = () => import('@/views/HomeView.vue');
const DemoStatsAndCharts = () => import('@/views/DemoStatsAndCharts.vue');
const DiagnosisBatteryTest = () => import('@/views/DiagnosisBatteryTest.vue');
const DiagnosisCheckCable = () => import('@/views/DiagnosisCheckCable.vue');
const Users = () => import('@/views/UsersList.vue');
const UserEdit = () => import('@/views/UserEdit.vue');
const UserCreate = () => import('@/views/UserCreate.vue');
const EmailBlacklist = () => import('@/views/EmailBlacklist.vue');
const SigninView = () => import('@/views/SigninView.vue');
const ForgotPassword = () => import('@/views/ForgotPassword.vue');
const UserVerification = () => import('@/views/UserVerification.vue');
const ResetPasswordVerification = () =>
    import('@/views/ResetPasswordVerification.vue');
const VerifyUser = () => import('@/views/VerifyUser.vue');
const ResetPassword = () => import('@/views/ResetPassword.vue');
const ChangePassword = () => import('@/views/ChangePassword.vue');
const PrivacyPolicy = () => import('@/views/PrivacyPolicy.vue');
const UserBatchCreate = () => import('@/views/UserBatchCreate.vue');
const UserBatchDelete = () => import('@/views/UserBatchDelete.vue');
const TestReport = () => import('@/views/TestReport.vue');
const CableDiagnosis = () => import('@/views/CableDiagnosis.vue');
const ShopManagement = () => import('@/views/ShopManagement.vue');
const DiagnosisDualBatteryTest = () =>
    import('@/views/DiagnosisDualBatteryTest.vue');
const IOTTestRecords = () => import('@/views/IOTTestRecords.vue');
const DeviceManagement = () => import('@/views/DeviceManagement.vue');
const RegisterView = () => import('@/views/RegisterView.vue');
const ClubInvitation = () => import('@/views/ClubInvitation.vue');
const AdvancedDashboard = () => import('@/views/AdvancedDashboard.vue');

import store from '@/store/index.js';

const t = i18n.global.t;

const routes = [
    {
        path: '/',
        name: 'signin',
        component: SigninView,
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: ForgotPassword,
        props: true,
    },
    {
        path: '/verify-user-token/:token',
        name: 'verify-user-token',
        component: UserVerification,
        props: true,
    },
    {
        path: '/verify-user/:token',
        name: 'verify-user',
        component: VerifyUser,
        props: true,
    },
    {
        path: '/verify-reset-password-token/:token',
        name: 'verify-reset-password-token',
        component: ResetPasswordVerification,
        props: true,
    },
    {
        path: '/club-invitation/:token',
        name: 'club-invitation',
        component: ClubInvitation,
        props: true,
    },
    {
        path: '/reset-password/:token',
        name: 'reset-password',
        component: ResetPassword,
        props: true,
    },
    {
        path: '/dashboard',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'home',
                name: 'Home',
                component: HomeView,
            },
            {
                path: 'demo-stats-charts',
                name: 'DemoStatsAndCharts',
                component: DemoStatsAndCharts,
                props: route => ({
                    roleView: route.query.role || null,
                }),
            },
            {
                path: 'advanced',
                name: 'AdvancedDashboard',
                component: AdvancedDashboard,
            },
        ],
    },
    {
        path: '/users',
        name: 'Users',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'list',
                name: 'List all users',
                component: Users,
            },
            {
                path: 'create',
                name: 'Create a user',
                component: UserCreate,
            },
            {
                path: 'create-employee',
                name: 'Create an employee user',
                component: UserCreate,
                props: { userAccountLevel: 'Employee' },
                key: 'create-employee',
            },
            {
                path: 'create-business',
                name: 'Create a business user',
                component: UserCreate,
                props: { userAccountLevel: 'Business' },
                key: 'create-business',
            },
            {
                path: 'edit/:id',
                name: 'Edit user',
                component: UserEdit,
            },
            {
                path: 'change-password',
                name: 'Change password',
                component: ChangePassword,
            },
            {
                path: 'batch-create',
                name: 'Batch create users',
                component: UserBatchCreate,
            },
            {
                path: 'batch-delete',
                name: 'Batch delete users',
                component: UserBatchDelete,
            },
        ],
    },
    {
        path: '/diagnosis',
        name: 'Diagnosis',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'battery-test',
                name: 'Battery Test',
                component: DiagnosisBatteryTest,
            },
            {
                path: 'check-cable',
                name: 'Check Cable',
                component: DiagnosisCheckCable,
            },
            {
                path: 'dual-battery-test',
                name: 'Dual Battery Test',
                component: DiagnosisDualBatteryTest,
            },
        ],
    },
    {
        path: '/configs',
        name: 'Configs',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'email-blacklist',
                name: 'Email blacklist',
                component: EmailBlacklist,
            },
        ],
    },
    {
        path: '/shopManagement',
        name: 'Shop Management',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'list',
                name: 'List all shops',
                component: ShopManagement,
            },
        ],
    },
    {
        path: '/sign-in/change-password',
        name: 'Signin and change password',
        component: ChangePassword,
        props: { formTitle: 'login.login_msg__account_default_password' },
    },
    {
        path: '/sign-in/change-expired-password',
        name: 'Signin and change expired password',
        component: ChangePassword,
    },
    {
        path: '/privacy-policy',
        name: 'Privacy Policy',
        component: PrivacyPolicy,
        meta: { requiresAuth: false },
    },
    {
        path: '/test-report/:uuid/:integrationKey/:clubId',
        name: 'Test Report',
        component: TestReport,
        meta: { requiresAuth: false },
        props: true,
    },
    {
        path: '/cable-diagnosis/:uuid/:integrationKey/:clubId',
        name: 'Cable Diagnosis',
        component: CableDiagnosis,
        meta: { requiresAuth: false },
        props: true,
    },
    {
        path: '/iot',
        name: 'IoT',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            // index (supports query: ?device_model=xxx&test_type=yyy)
            {
                path: '',
                name: 'IOTTestRecords',
                component: IOTTestRecords,
            },
            // /iot/:device_model
            {
                path: ':device_model',
                name: 'IOTTestRecordsByModel',
                component: IOTTestRecords,
            },
            // /iot/:device_model/:test_type
            {
                path: ':device_model/:test_type',
                name: 'IOTTestRecordsByModelAndType',
                component: IOTTestRecords,
            },
            {
                path: 'deviceManagement',
                name: 'Device Management',
                component: DeviceManagement,
            },
        ],
    },
    {
        path: '/register',
        name: 'register',
        component: RegisterView,
    },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});

const ensureLocaleMessagesLoaded = async () => {
    const locale = store.getters['i18n/locale'] || 'en';
    const messages = store.getters['i18n/messages'];

    if (!messages[locale]) {
        const response = await apiClient.get(
            `/languages/lang/${locale}?disable_cache=false`,
            { skipAuthorization: true }
        );

        i18n.global.setLocaleMessage(locale, response.data);
        i18n.global.locale.value = locale;
        setI18nLocale(locale, response.data);
        store.commit('i18n/setLocale', { locale, messages: response.data });
    }
};

router.beforeEach(async (to, from, next) => {
    setLoading(true);
    await ensureLocaleMessagesLoaded();

    if (to.matched.some(record => record.meta.requiresAuth)) {
        if (!store.getters['auth/isAuthenticated']) {
            Swal.fire({
                title: t('django.system_msg__status_code_401'),
                text: 'You need to sign in to access this page',
                icon: 'warning',
                confirmButtonText: 'OK',
            }).then(result => {
                if (result.isConfirmed) {
                    next({ path: '/' });
                }
            });
        } else {
            next();
        }
    } else {
        next();
    }
});

router.afterEach(() => {
    setLoading(false);
});

router.onError(() => {
    setLoading(false);
});

export default router;
